/*
 * Copyright (c) 2025.
 * This code and its derivatives belong to BETICA LTD and may not be copied, reproduced, amended or used in any way without the permission of BETICA LTD
 * @author torrejonv
 *
 */

/**
 * @file countrySlice.js
 * @description Redux slice for managing country related state and operations
 */

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import createAxiosInstance from "../async/axios";
import { getRequestParams } from "../async/get-fetch-params";

/**
 * @function getInitialState
 * @description Creates the initial state object for the country slice
 * @param {Object} params - Initial state parameters
 * @returns {Object} The initial state object
 */
export const getInitialState = (countries = null) => ({
  countries,
  error: null,
  loading: false,
});

/**
 * @function getCountries
 * @description Async thunk for get countries operations
 * @param {Object} data - Parameters for the operation
 * @returns {Promise<Object>} Result of the operation
 */
export const getCountries = createAsyncThunk("country/loadCountries", async (data, thunkAPI) => {
  try {
    const { authToken, language, lineId, originId } = getRequestParams(thunkAPI.getState());
    const axios = createAxiosInstance(thunkAPI.dispatch, {
      authToken,
      language,
    });

    const result = await axios.get(`/player/countries?originId=${originId}&lineId=${lineId}`);

    return {
      countries: result.data,
    };
  } catch (err) {
    const customError = {
      message: err.response?.headers["x-information"] || "Unable to obtain country details", // serializable (err.response.data)
      name: "Country list Fetch Error",
      status: err.response?.statusText,
    };
    throw customError;
  }
});

/**
 * @constant countrySlice
 * @description Redux slice for country management
 */
const countrySlice = createSlice({
  extraReducers: (builder) => {
    builder
      .addCase(getCountries.pending, (state) => {
        state.countries = null;
        state.error = null;
        state.loading = true;
      })
      .addCase(getCountries.rejected, (state, action) => {
        state.error = action.error.message;
        state.loading = false;
      })
      .addCase(getCountries.fulfilled, (state, action) => {
        state.countries = action.payload.countries;
        state.loading = false;
        state.error = null;
      });
  },
  initialState: getInitialState(),
  name: "country",
  reducers: {},
});
const { reducer } = countrySlice;
export default reducer;
